import { LiaTransparentElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { request } from '../../shared/api/http.js';
import { openPaddle } from '../paddle.js';

/** Paddle email/payment links work without an app session; they never bind or grant seats. */
export class UserPayment extends LiaTransparentElement
{
   static properties = {
      transactionId: { attribute: false },
      state: { state: true },
      message: { state: true }
   };

   constructor()
   {
      super();
      this.transactionId = '';
      this.state = 'loading';
      this.message = null;
      this.frameTarget = 'paddle-link-' + crypto.randomUUID();
      this._attempt = 0;
   }

   updated(changed)
   {
      if (changed.has('transactionId')) void this.open();
   }

   disconnectedCallback()
   {
      this.stop();
      super.disconnectedCallback();
   }

   stop()
   {
      this._attempt++;
      this._abort?.abort();
      clearTimeout(this._timeout);
      this._close?.();
      this._close = null;
   }

   async open()
   {
      this.stop();
      if (!/^txn_[a-z0-9]{26}$/.test(this.transactionId))
      {
         this.state = 'invalid';
         return;
      }
      const attempt = this._attempt;
      const current = () => this.isConnected && attempt === this._attempt;
      this._abort = new AbortController();
      this.state = 'loading';
      this.message = null;
      // Covers both the public configuration request and the payment frame loading.
      this._timeout = setTimeout(() =>
      {
         if (!current()) return;
         this.stop();
         this.state = 'error';
         this.message = 'The payment form took too long to load. Check your connection or content blocker, then try again.';
      }, 30000);
      try
      {
         const config = await request('/api/v1/client-config', { auth: false, signal: this._abort.signal });
         if (!current()) return;
         if (!config.paddle?.clientToken) throw new Error('Payments are temporarily unavailable. Please try again later or contact support.');
         await this.updateComplete;
         const close = await openPaddle(config.paddle, {
            transactionId: this.transactionId,
            settings: {
               displayMode: 'inline', variant: 'one-page', frameTarget: this.frameTarget,
               frameInitialHeight: '450', frameStyle: 'width:100%; min-width:312px; background-color:transparent; border:none;',
               theme: document.documentElement.dataset.bsTheme === 'dark' ? 'dark' : 'light'
            }
         }, event => { if (current()) this.onPayment(event); }, current);
         if (!current()) close();
         else this._close = close;
      }
      catch (error)
      {
         if (!current()) return;
         this.stop();
         this.state = 'error';
         this.message = error.message || 'The payment form could not be loaded. Please try again.';
      }
   }

   onPayment(event)
   {
      if (event.name === 'checkout.error' && !event.data?.transaction_id)
      {
         this.stop();
         this.state = 'error';
         this.message = 'This payment could not be opened. It may already be completed or no longer available. Check your billing page before trying again.';
         return;
      }
      if (event.data?.transaction_id !== this.transactionId) return;
      if (['checkout.loaded', 'checkout.updated'].includes(event.name))
      {
         clearTimeout(this._timeout);
         this.state = 'payment';
         this.message = null;
         this.querySelector(`.${this.frameTarget} iframe`)?.setAttribute('title', 'Secure payment form by Paddle');
      }
      if (event.name === 'checkout.error')
      {
         clearTimeout(this._timeout);
         this.message = 'Payment could not be completed. Check the message in the payment form.';
      }
      if (event.name === 'checkout.completed')
      {
         this.stop();
         this.state = 'complete';
         this.message = null;
      }
   }

   render()
   {
      return html`
         <lia-auth-layout .showThemeToggle=${false} brand="covdbg" brand-icon="fa-solid fa-bug"
            heading=${this.state === 'complete' ? 'Submitted to Paddle' : 'Secure payment'}
            description="Payments and payment details are handled securely by Paddle." max-width="640">
            <div aria-live="polite">
               ${this.state === 'loading' ? html`<p class="text-body-secondary">Loading your payment form…</p>` : nothing}
               ${this.state === 'invalid' ? html`<lia-alert variant="warning" heading="Payment link missing or incomplete"
                  message="Open the full payment link from your Paddle email, or sign in to manage billing."></lia-alert>` : nothing}
               ${this.message ? html`<lia-alert variant="warning" message=${this.message}></lia-alert>` : nothing}
               ${this.state === 'complete' ? html`<lia-alert variant="success" heading="You can close this page"
                  message="Your billing status will update after confirmation. Sign in to check your team's billing page if needed."></lia-alert>` : nothing}
               ${this.state === 'error' ? html`<button class="btn btn-primary" @click=${() => this.open()}>Try again</button>` : nothing}
            </div>
            <div class=${this.frameTarget} ?hidden=${!['loading', 'payment'].includes(this.state)}></div>
            <div class="d-flex flex-wrap gap-3 mt-3 small">
               <a href="/">Go to your account</a>
               <a href="https://covdbg.com/company/contact/">Contact billing support</a>
            </div>
         </lia-auth-layout>`;
   }
}

customElements.define('user-payment', UserPayment);
